import { Spinner } from '@cimplico/components';
import { useFirmContext } from '../../providers/FirmProvider';
import { useFirmInsights } from '../../queries/use-firm-insights';
import ErrorSplashScreen from '../../pages/ErrorSplashScreen';
import ActiveWork from './active-work/ActiveWork';
import CompletedWork from './completed-work/CompletedWork';
import FirmInsightsPreview from '../../components/preview/FirmInsightsPreview';
import { useHasFeatureAccess } from '../../hooks/use-has-feature-access';

type FirmInsightsProps = {
    filter?: string;
    subscriptionTierRequired?: boolean;
    fullPage?: boolean;
};

function FirmInsights({ filter, subscriptionTierRequired = false, fullPage = false }: FirmInsightsProps): JSX.Element {
    const { id } = useFirmContext();
    const canAccessFirmInsights = useHasFeatureAccess('firm-insights:read');

    // if not on firm dashboard page, check for subscription tier before getting firm insights
    const firmInsightsQuery = useFirmInsights(!canAccessFirmInsights && subscriptionTierRequired ? undefined : id, { filter });

    if (!canAccessFirmInsights && subscriptionTierRequired) {
        return <FirmInsightsPreview />;
    }

    if (firmInsightsQuery.isLoading) {
        return (
            <div className="absolute w-full mx-8 xl:mx-auto max-w-[1240px] h-[var(--remaining-screen-height)]">
                <div className={`absolute ${fullPage ? 'top-1/3' : 'top-[15%]'} left-1/2`}>
                    <Spinner />
                </div>
            </div>
        );
    }

    if (firmInsightsQuery.isError || !firmInsightsQuery.isSuccess) {
        if (firmInsightsQuery.error?.response?.status === 404) {
            return (
                <ErrorSplashScreen
                    errorCode={404}
                    title="User not found."
                    redirectTitle="Back to onboarding"
                    redirectUrl={`/onboarding`}
                    leftArrow={true}
                />
            );
        }
        return (
            <ErrorSplashScreen
                title="An error has occurred."
                redirectTitle="Back to onboarding"
                redirectUrl={`/onboarding`}
                leftArrow={true}
            />
        );
    }

    const { activeWork, completedWork, overdueWorkpapers } = firmInsightsQuery.data.insights;

    return (
        <div className="grid gap-y-8">
            <CompletedWork completedWork={completedWork} />
            <ActiveWork activeWork={activeWork} overdueWorkpapers={overdueWorkpapers} />
        </div>
    );
}

export default FirmInsights;
